import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import Popup from '../components/Popup';

/* ─── styles ─────────────────────────────────────────────────────────────── */
const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');

  .ad-page {
    font-family: 'Inter', system-ui, sans-serif;
    background: #0a0f1e;
    min-height: 100vh;
    padding: 32px 24px 80px;
    color: #e2e8f0;
  }
  .ad-inner { max-width: 1200px; margin: 0 auto; }

  /* ── heading ── */
  .ad-heading { display: flex; align-items: center; gap: 14px; margin-bottom: 32px; flex-wrap: wrap; }
  .ad-badge {
    display: inline-flex; align-items: center; gap: 5px;
    font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em;
    background: rgba(239,68,68,0.1); border: 1px solid rgba(239,68,68,0.25); color: #fca5a5;
    padding: 4px 12px; border-radius: 100px;
  }
  .ad-title { font-size: 1.7rem; font-weight: 900; color: #f1f5f9; letter-spacing: -0.03em; margin: 0; }

  /* ── alert ── */
  .ad-alert {
    background: rgba(239,68,68,0.1); border: 1px solid rgba(239,68,68,0.25); color: #fca5a5;
    border-radius: 10px; padding: 12px 16px; font-size: 14px; margin-bottom: 24px;
  }

  /* ── stat cards ── */
  .ad-stats { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 16px; margin-bottom: 28px; }
  .ad-stat {
    background: rgba(99,102,241,0.07); border: 1px solid rgba(99,102,241,0.15);
    border-radius: 18px; padding: 22px;
  }
  .ad-stat-label { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; color: #475569; margin: 0 0 10px; }
  .ad-stat-num   { font-size: 2.4rem; font-weight: 900; color: #f1f5f9; letter-spacing: -0.04em; line-height: 1; margin: 0; }

  /* ── table card ── */
  .ad-card {
    background: rgba(255,255,255,0.025); border: 1px solid rgba(99,102,241,0.13);
    border-radius: 20px; padding: 24px; overflow: hidden;
  }
  .ad-card-title { font-size: 1rem; font-weight: 800; color: #f1f5f9; margin: 0 0 20px; }

  .ad-table { width: 100%; border-collapse: collapse; font-size: 13px; min-width: 680px; }
  .ad-table th {
    font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.07em;
    color: #475569; padding: 8px 12px; text-align: left;
    border-bottom: 1px solid rgba(99,102,241,0.14); cursor: pointer; user-select: none;
    white-space: nowrap;
  }
  .ad-table th:hover { color: #94a3b8; }
  .ad-table td { padding: 11px 12px; color: #94a3b8; border-bottom: 1px solid rgba(255,255,255,0.04); vertical-align: middle; }
  .ad-table tbody tr:hover td { background: rgba(99,102,241,0.05); }
  .ad-table tbody tr:last-child td { border-bottom: none; }

  .ad-username { color: #e2e8f0; font-weight: 700; }
  .ad-email    { font-size: 12.5px; }

  /* role / plan / status pills */
  .ad-pill {
    display: inline-block; font-size: 10.5px; font-weight: 700; text-transform: uppercase;
    letter-spacing: 0.06em; padding: 2px 9px; border-radius: 100px;
  }
  .ad-pill-admin { background: rgba(239,68,68,0.12); border: 1px solid rgba(239,68,68,0.25);  color: #fca5a5; }
  .ad-pill-user  { background: rgba(100,116,139,0.1); border: 1px solid rgba(100,116,139,0.2); color: #64748b; }
  .ad-pill-pro   { background: rgba(99,102,241,0.12); border: 1px solid rgba(99,102,241,0.22); color: #a5b4fc; }
  .ad-pill-free  { background: rgba(100,116,139,0.08);border: 1px solid rgba(100,116,139,0.15); color: #475569; }

  /* links count — clickable */
  .ad-links-btn {
    background: rgba(99,102,241,0.1); border: 1px solid rgba(99,102,241,0.2); color: #a5b4fc;
    border-radius: 7px; padding: 3px 10px; font-size: 12.5px; font-weight: 700;
    cursor: pointer; font-family: inherit; transition: all 0.2s;
  }
  .ad-links-btn:hover { background: rgba(99,102,241,0.2); }
  .ad-links-zero { color: #334155; font-size: 13px; }

  /* ── pagination ── */
  .ad-pagination { display: flex; justify-content: space-between; align-items: center; margin-top: 20px; }
  .ad-page-info  { font-size: 13px; color: #475569; }
  .ad-page-btns  { display: flex; gap: 8px; }

  .ad-page-btn {
    padding: 7px 16px; border-radius: 9px; font-size: 13px; font-weight: 700;
    cursor: pointer; font-family: inherit; transition: all 0.2s;
    background: rgba(99,102,241,0.1); border: 1px solid rgba(99,102,241,0.2); color: #a5b4fc;
  }
  .ad-page-btn:hover:not(:disabled) { background: rgba(99,102,241,0.2); }
  .ad-page-btn:disabled { opacity: 0.3; cursor: not-allowed; }

  /* ── modal ── */
  .ad-modal-overlay {
    position: fixed; inset: 0; background: rgba(0,0,0,0.72);
    backdrop-filter: blur(8px); display: flex; align-items: center; justify-content: center;
    z-index: 1000; padding: 20px;
  }
  .ad-modal {
    background: #131929; border: 1px solid rgba(99,102,241,0.22); border-radius: 20px;
    padding: 28px; width: 100%; max-width: 860px; max-height: 85vh;
    overflow-y: auto; box-shadow: 0 28px 60px rgba(0,0,0,0.55);
    animation: adFadeUp 0.25s ease-out both;
  }
  @keyframes adFadeUp {
    from { opacity: 0; transform: translateY(14px) scale(0.98); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
  }

  .ad-modal-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; }
  .ad-modal-title { font-size: 1.1rem; font-weight: 900; color: #f1f5f9; margin: 0; }
  .ad-modal-close {
    background: rgba(255,255,255,0.05); border: 1px solid rgba(99,102,241,0.15);
    color: #64748b; border-radius: 8px; width: 32px; height: 32px;
    display: inline-flex; align-items: center; justify-content: center;
    font-size: 18px; cursor: pointer; line-height: 1; font-family: inherit;
    transition: all 0.2s;
  }
  .ad-modal-close:hover { background: rgba(239,68,68,0.1); color: #fca5a5; }

  .ad-link-url {
    color: #818cf8; font-size: 12.5px; text-decoration: none; word-break: break-all;
  }
  .ad-link-url:hover { text-decoration: underline; }

  .ad-status-active   { background: rgba(34,197,94,0.1);  border: 1px solid rgba(34,197,94,0.2);  color: #86efac; }
  .ad-status-inactive { background: rgba(239,68,68,0.1);  border: 1px solid rgba(239,68,68,0.2);  color: #fca5a5; }

  /* loading state */
  .ad-loading {
    background: #0a0f1e; min-height: 100vh;
    display: flex; align-items: center; justify-content: center;
    font-family: 'Inter', sans-serif; color: #475569; font-size: 15px;
  }

  /* reserved words panel */
  .ad-rw-grid { display: flex; flex-wrap: wrap; gap: 8px; }
    .ad-rw-chip {
      display: inline-flex; align-items: center;
      background: rgba(99,102,241,0.08); border: 1px solid rgba(99,102,241,0.18);
      border-radius: 100px; padding: 4px 12px; font-size: 12px; font-weight: 600; color: #a5b4fc;
    }

    /* delete user button */
    .ad-delete-btn {
      background: rgba(239,68,68,0.1); border: 1px solid rgba(239,68,68,0.2); color: #fca5a5;
      border-radius: 7px; padding: 3px 10px; font-size: 12px; font-weight: 700;
      cursor: pointer; font-family: inherit; transition: all 0.2s;
    }
    .ad-delete-btn:hover { background: rgba(239,68,68,0.2); border-color: rgba(239,68,68,0.3); }
  `;

/* ─── component ──────────────────────────────────────────────────────────── */
const AdminDashboard = () => {
    const { user } = useAuth();
    const navigate = useNavigate();

    const [stats, setStats] = useState({ totalUsers: 0, totalLinks: 0, totalClicks: 0 });
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [sortBy, setSortBy] = useState('created_at');
    const [order, setOrder] = useState('DESC');
    const limit = 20;

    const [selectedUser, setSelectedUser] = useState(null);
    const [userLinks, setUserLinks] = useState([]);
    const [linksLoading, setLinksLoading] = useState(false);
    const [linksPage, setLinksPage] = useState(1);
    const [linksTotalPages, setLinksTotalPages] = useState(1);

    // Reserved words state (read-only, synced from reservedCodes.json)
    const [reservedWords, setReservedWords] = useState([]);

    // Delete confirmation state
    const [showDeletePopup, setShowDeletePopup] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    useEffect(() => {
        if (user && user.role !== 'admin') { navigate('/dashboard'); return; }
        fetchAdminData();
        fetchReservedWords();
    }, [user, navigate, page, sortBy, order]);

    const fetchAdminData = async () => {
        setLoading(true);
        try {
            const [statsRes, usersRes] = await Promise.all([
                axios.get(`${API_BASE_URL}/api/admin/stats`),
                axios.get(`${API_BASE_URL}/api/admin/users?page=${page}&limit=${limit}&sortBy=${sortBy}&order=${order}`),
            ]);
            setStats(statsRes.data);
            setUsers(usersRes.data.users);
            setTotalPages(usersRes.data.totalPages);
        } catch {
            setError('Failed to fetch admin data');
        } finally {
            setLoading(false);
        }
    };

    const fetchUserLinks = async (userId, username, pageNum = 1) => {
        setSelectedUser({ id: userId, username });
        setLinksLoading(true);
        setLinksPage(pageNum);
        try {
            const r = await axios.get(`${API_BASE_URL}/api/admin/users/${userId}/links?page=${pageNum}&limit=10`);
            setUserLinks(r.data.links);
            setLinksTotalPages(r.data.totalPages);
        } catch { /* silently ignore */ }
        finally { setLinksLoading(false); }
    };

    const fetchReservedWords = async () => {
        try {
            const r = await axios.get(`${API_BASE_URL}/api/admin/reserved-words`);
            setReservedWords(r.data.words);
        } catch { /* silently ignore */ }
    };

    const confirmDelete = (u) => {
        setUserToDelete(u);
        setShowDeletePopup(true);
    };

    const handleDelete = async () => {
        if (!userToDelete) return;
        setIsDeleting(true);
        try {
            await axios.delete(`${API_BASE_URL}/api/admin/users/${userToDelete.id}`);
            setUsers(prev => prev.filter(u => u.id !== userToDelete.id));
            setShowDeletePopup(false);
            setUserToDelete(null);
            // Re-fetch stats to reflect the change
            const statsRes = await axios.get(`${API_BASE_URL}/api/admin/stats`);
            setStats(statsRes.data);
        } catch (err) {
            const msg = err.response?.data?.error || 'Deletion failed';
            alert(msg);
        } finally {
            setIsDeleting(false);
        }
    };



    const getShortUrl = (link) => {
        const { short_code, url_type, brand_name } = link;
        if (url_type === 'brand_path' && brand_name) {
            return `${API_BASE_URL}/${brand_name}/${short_code}`;
        } else if (url_type === 'brand_subdomain' && brand_name) {
            const base = API_BASE_URL.replace('://', `://${brand_name}.`);
            return `${base}/${short_code}`;
        }
        return `${API_BASE_URL}/${short_code}`;
    };

    const getShortUrlLabel = (link) => {
        const { short_code, url_type, brand_name } = link;
        const host = window.location.hostname === 'localhost' ? 'rdrto.com' : window.location.hostname;
        if (url_type === 'brand_path' && brand_name) {
            return `${host}/${brand_name}/${short_code}`;
        } else if (url_type === 'brand_subdomain' && brand_name) {
            return `${brand_name}.${host}/${short_code}`;
        }
        return `${host}/${short_code}`;
    };

    const handleSort = (col) => {
        if (sortBy === col) setOrder(o => o === 'ASC' ? 'DESC' : 'ASC');
        else { setSortBy(col); setOrder('DESC'); }
        setPage(1);
    };

    const sortIcon = (col) => sortBy === col ? (order === 'ASC' ? ' ↑' : ' ↓') : '';

    if (loading && !users.length) return <div className="ad-loading"><style>{STYLES}</style>Loading admin dashboard…</div>;

    return (
        <div className="ad-page">
            <style>{STYLES}</style>
            <div className="ad-inner">

                {/* ── Heading ── */}
                <div className="ad-heading">
                    <span className="ad-badge">⚙ Admin</span>
                    <h1 className="ad-title">Admin Dashboard</h1>
                </div>

                {error && <div className="ad-alert">{error}</div>}

                {/* ── Stat tiles ── */}
                <div className="ad-stats">
                    <div className="ad-stat">
                        <p className="ad-stat-label">Total Users</p>
                        <p className="ad-stat-num">{stats.totalUsers}</p>
                    </div>
                    <div className="ad-stat">
                        <p className="ad-stat-label">Total Links</p>
                        <p className="ad-stat-num">{stats.totalLinks}</p>
                    </div>
                    <div className="ad-stat">
                        <p className="ad-stat-label">Total Clicks</p>
                        <p className="ad-stat-num">{stats.totalClicks}</p>
                    </div>
                </div>

                {/* ── Users table ── */}
                <div className="ad-card">
                    <h2 className="ad-card-title">All Users</h2>
                    <div style={{ overflowX: 'auto' }}>
                        <table className="ad-table">
                            <thead>
                                <tr>
                                    <th onClick={() => handleSort('id')}>ID{sortIcon('id')}</th>
                                    <th onClick={() => handleSort('username')}>Username{sortIcon('username')}</th>
                                    <th onClick={() => handleSort('email')}>Email{sortIcon('email')}</th>
                                    <th onClick={() => handleSort('role')}>Role{sortIcon('role')}</th>
                                    <th onClick={() => handleSort('plan')}>Plan{sortIcon('plan')}</th>
                                    <th>Brand</th>
                                    <th onClick={() => handleSort('link_count')}>Links{sortIcon('link_count')}</th>
                                    <th onClick={() => handleSort('created_at')}>Joined{sortIcon('created_at')}</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {users.map(u => (
                                    <tr key={u.id}>
                                        <td style={{ color: '#334155' }}>{u.id}</td>
                                        <td><span className="ad-username">{u.username}</span></td>
                                        <td><span className="ad-email">{u.email}</span></td>
                                        <td>
                                            <span className={`ad-pill ${u.role === 'admin' ? 'ad-pill-admin' : 'ad-pill-user'}`}>
                                                {u.role}
                                            </span>
                                        </td>
                                        <td>
                                            <span className={`ad-pill ${u.plan === 'pro' ? 'ad-pill-pro' : u.plan === 'business' ? 'ad-pill-pro' : 'ad-pill-free'}`}>
                                                {u.plan}
                                            </span>
                                        </td>
                                        <td>
                                            <span style={{ fontSize: 12, color: u.brand_name ? '#fbbf24' : '#334155' }}>
                                                {u.brand_name || '—'}
                                            </span>
                                        </td>
                                        <td>
                                            {parseInt(u.link_count) > 0 ? (
                                                <button className="ad-links-btn" onClick={() => fetchUserLinks(u.id, u.username)}>
                                                    {u.link_count}
                                                </button>
                                            ) : (
                                                <span className="ad-links-zero">—</span>
                                            )}
                                        </td>
                                        <td>{new Date(u.created_at).toLocaleDateString()}</td>
                                        <td>
                                            {u.id !== user.id && (
                                                <button
                                                    className="ad-delete-btn"
                                                    onClick={() => confirmDelete(u)}
                                                >
                                                    Delete
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    <div className="ad-pagination">
                        <span className="ad-page-info">Page {page} of {totalPages}</span>
                        <div className="ad-page-btns">
                            <button className="ad-page-btn" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>← Prev</button>
                            <button className="ad-page-btn" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>Next →</button>
                        </div>
                    </div>
                </div>

                {/* ── User links modal ── */}
                {selectedUser && (
                    <div className="ad-modal-overlay" onClick={() => setSelectedUser(null)}>
                        <div className="ad-modal" onClick={e => e.stopPropagation()}>
                            <div className="ad-modal-header">
                                <h2 className="ad-modal-title">🔗 Links for <span style={{ color: '#818cf8' }}>{selectedUser.username}</span></h2>
                                <button className="ad-modal-close" onClick={() => setSelectedUser(null)}>×</button>
                            </div>

                            {linksLoading ? (
                                <p style={{ color: '#475569', fontSize: 14 }}>Loading links…</p>
                            ) : (
                                <div style={{ overflowX: 'auto' }}>
                                    <table className="ad-table" style={{ minWidth: 600 }}>
                                        <thead>
                                            <tr>
                                                <th>Short Code</th>
                                                <th>Target URL</th>
                                                <th>Clicks</th>
                                                <th>Status</th>
                                                <th>Created</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {userLinks.length === 0 ? (
                                                <tr><td colSpan={5} style={{ textAlign: 'center', color: '#334155', padding: 20 }}>No links found.</td></tr>
                                            ) : userLinks.map(link => (
                                                <tr key={link.id}>
                                                    <td style={{ fontWeight: 700 }}>
                                                        <a
                                                            className="ad-link-url"
                                                            href={getShortUrl(link)}
                                                            target="_blank" rel="noopener noreferrer"
                                                        >
                                                            {getShortUrlLabel(link)}
                                                        </a>
                                                    </td>
                                                    <td style={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                        {link.long_url}
                                                    </td>
                                                    <td>
                                                        <span style={{ color: '#f1f5f9', fontWeight: 700 }}>{link.total_clicks}</span>
                                                    </td>
                                                    <td>
                                                        <span className={`ad-pill ${link.is_active ? 'ad-status-active' : 'ad-status-inactive'}`}>
                                                            {link.is_active ? 'Active' : 'Inactive'}
                                                        </span>
                                                    </td>
                                                    <td>{new Date(link.created_at).toLocaleDateString()}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>

                                    {/* Pagination for User Links */}
                                    {linksTotalPages > 1 && (
                                        <div className="ad-pagination" style={{ marginTop: 24, padding: '0 10px' }}>
                                            <span className="ad-page-info">Page {linksPage} of {linksTotalPages}</span>
                                            <div className="ad-page-btns">
                                                <button
                                                    className="ad-page-btn"
                                                    onClick={() => fetchUserLinks(selectedUser.id, selectedUser.username, linksPage - 1)}
                                                    disabled={linksPage === 1}
                                                >← Prev</button>
                                                <button
                                                    className="ad-page-btn"
                                                    onClick={() => fetchUserLinks(selectedUser.id, selectedUser.username, linksPage + 1)}
                                                    disabled={linksPage === linksTotalPages}
                                                >Next →</button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* ── Reserved Words panel (read-only) ── */}
                <div className="ad-card" style={{ marginTop: 28 }}>
                    <h2 className="ad-card-title">🚫 Reserved Words <span style={{ fontSize: 12, fontWeight: 500, color: '#475569' }}>({reservedWords.length})</span></h2>
                    <p style={{ fontSize: 13, color: '#475569', marginBottom: 16 }}>
                        These words cannot be used as custom slugs or brand names. Managed via <code style={{ color: '#818cf8', fontSize: 12 }}>reservedCodes.json</code> — edit the file and redeploy to update.
                    </p>
                    <div className="ad-rw-grid">
                        {reservedWords.map(w => (
                            <span key={w.id} className="ad-rw-chip">{w.word}</span>
                        ))}
                        {reservedWords.length === 0 && <span style={{ color: '#334155', fontSize: 13 }}>No reserved words.</span>}
                    </div>
                </div>
            </div>

            {/* ── Delete User Confirmation Popup ── */}
            {showDeletePopup && (
                <Popup
                    isOpen={true}
                    type="danger"
                    title="Delete User?"
                    message={`Are you sure you want to permanently delete user **${userToDelete?.username}** (${userToDelete?.email})? This action cannot be undone and all their links will be gone.`}
                    confirmText={isDeleting ? "Deleting..." : "Permanently Delete"}
                    onConfirm={handleDelete}
                    onCancel={() => !isDeleting && setShowDeletePopup(false)}
                />
            )}

        </div>
    );
};

export default AdminDashboard;
